import {
    Component,
    Input,
    OnInit,
    OnDestroy,
    Output,
    EventEmitter
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    FormControl,
    Validators,
    ReactiveFormsModule
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BookingService } from '@app/_services/booking.service';
import { ToastrService } from 'ngx-toastr';
import { Product } from '@app/_interfaces/product';
import moment from 'moment';
import { CalendarMonth, CalendarDay } from '@app/_interfaces/calendar';
import { CompaniesService } from '@app/_services';
import { QuotationRequestService } from '@app/_services/quotation-request.service';
import { AuthenticationService } from '@app/_services';
import { Router } from '@angular/router';
import { InputNumberNewComponent } from '@app/shared/components/input-number-new/input-number-new.component';
import { CartService } from '@services/cart.service'; // Add cart service
import { FormDataHelper } from '@app/_helpers'; // Add form data helper
import { CurrencyConvertPipe } from '@app/shared/pipes';
import * as _ from 'lodash';

/**
 * PerDayBookingComponent - Interactive calendar component for per-day bookings
 *
 * This component provides a comprehensive booking interface for products that support
 * per-day booking type. Users can select multiple individual dates (non-consecutive)
 * and the component handles availability checking, quantity management, and booking creation.
 *
 * Key Features:
 * - Interactive calendar with 3-month view
 * - Real-time availability checking with quantity tracking
 * - Multiple date selection with constraints
 * - Automatic price calculation
 * - Business rule validation (advance booking, max days per user)
 * - Responsive design with touch support
 *
 * Business Logic:
 * - Each booking is for quantity = 1 (simplified from original multi-quantity design)
 * - Supports products with multiple units (tracked via availableQuantityByDate)
 * - Enforces advance booking requirements
 * - Limits maximum days per user booking
 * - Prevents booking of fully booked dates
 *
 * User Experience:
 * - Visual indication of available/unavailable dates
 * - Hover tooltips with detailed availability information
 * - Clear constraints display
 * - Instant feedback on selection changes
 * - Loading states for better UX
 */
@Component({
    selector: 'app-per-day-booking',
    templateUrl: './per-day-booking.component.html',
    styleUrls: ['./per-day-booking.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        InputNumberNewComponent,
        CurrencyConvertPipe
    ]
})
export class PerDayBookingComponent implements OnInit, OnDestroy {
    // === INPUT/OUTPUT PROPERTIES ===

    /** Product being booked - contains all product and booking configuration */
    @Input() product: Product;

    /** Emits when a booking is successfully created */
    @Output() bookingCreated = new EventEmitter<any>();

    /** Emits when an item is added to cart */
    @Output() addedToCart = new EventEmitter<any>();

    /** Emits when the total price changes (for parent component price display) */
    @Output() priceCalculated = new EventEmitter<number>();

    /** Emits when selected dates change (for parent component synchronization) */
    @Output() selectedDatesChanged = new EventEmitter<string[]>();

    /** Emits when a quotation request is successfully created */
    @Output() quotationRequested = new EventEmitter<any>();

    // === FORM AND VALIDATION ===

    /** Reactive form for booking data with validation */
    bookingForm: FormGroup;

    /** Reactive form for quotation requests */
    quotationForm: FormGroup;

    /** Whether to show the quotation form modal */
    showQuotationForm = false;

    /** Form control for quantity selection */
    quantityControl: FormControl;

    /** Current selected quantity */
    get selectedQuantity(): number {
        return this.quantityControl?.value || 1;
    }

    // === AVAILABILITY DATA ===

    /** Array of dates that are available for booking (YYYY-MM-DD format) */
    availableDates: string[] = [];

    /** Array of unavailable dates with reasons for unavailability */
    unavailableDates: any[] = [];

    /** Array of currently selected dates by the user */
    selectedDates: string[] = [];

    /** Whether API requests are in progress */
    loading = false;

    /** Calculated total price for all selected dates */
    totalPrice = 0;

    /**
     * Tracks available quantity for each date
     * Key: date string (YYYY-MM-DD)
     * Value: number of items available for booking on that date
     */
    availableQuantityByDate: { [date: string]: number } = {};

    // === CALENDAR DISPLAY ===

    /** Current month index being displayed (0-2 for 3-month view) */
    currentMonthIndex = 0;

    /** Array of calendar months with all day information */
    calendarMonths: CalendarMonth[] = [];

    /** Week day headers for calendar display */
    weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    /** Number of future months to display in the calendar */
    monthsToGenerate = 1;

    /** Subject for handling component destruction and unsubscribing */
    private destroy$ = new Subject<void>();

    // === COMPUTED PROPERTIES ===

    /**
     * Gets the product's available quantity/stock
     * Used for calculating per-date availability
     *
     * @returns Number of items available for this product
     */
    get productQuantity(): number {
        const quantity = (this.product as any)?.quantity?.amount || 0;
        // Ensure quantity is never negative
        return Math.max(0, quantity);
    }

    /**
     * Checks if the product has any available quantity
     * @returns true if product has quantity > 0
     */
    get hasProductQuantity(): boolean {
        return this.productQuantity > 0;
    }

    /**
     * Gets the maximum number of days a user can book in a single booking
     * Used for enforcing booking limits and UI constraints
     *
     * @returns Maximum days per user booking (0 = no limit)
     */
    get maxDaysPerUser(): number {
        return this.product?.booking?.maxBookingsPerUser || 0;
    }

    /**
     * Checks if the product is set up for quotation instead of direct booking
     */
    get isQuotable(): boolean {
        return this.product?.isOnlyQuotation || false;
    }

    /**
     * Constructor - Sets up dependencies and initializes the booking form
     *
     * @param fb - FormBuilder for creating reactive forms
     * @param bookingService - Service for booking API calls
     * @param toastr - Service for displaying toast notifications
     * @param translate - Service for internationalization
     */
    constructor(
        private fb: FormBuilder,
        private bookingService: BookingService,
        private toastr: ToastrService,
        private translate: TranslateService,
        private companiesService: CompaniesService,
        private quotationRequestService: QuotationRequestService,
        private authService: AuthenticationService,
        private router: Router,
        private cartService: CartService, // Add cart service
        private formDataHelper: FormDataHelper // Add form data helper
    ) {
        this.initializeForm();
    }

    /**
     * Initializes the reactive form with validation rules
     */
    private initializeForm() {
        // Initialize quantity control
        this.quantityControl = new FormControl(
            this.product?.quantity?.min || 1,
            [
                Validators.min(this.product?.quantity?.min || 1),
                Validators.max(this.product?.quantity?.max || 99),
                Validators.required
            ]
        );

        this.bookingForm = this.fb.group({
            selectedDates: [
                [], // Default to empty array
                [
                    // Remove the conflicting validators - the array should not be required to have minLength
                    // when we want to allow empty state initially
                    control => {
                        const selectedDates = control.value || [];

                        // Check if at least one date is selected (but only when submitting)
                        if (selectedDates.length === 0) {
                            return { required: true };
                        }

                        // Check if max days limit is exceeded
                        const maxDays = this.maxDaysPerUser;
                        if (maxDays > 0 && selectedDates.length > maxDays) {
                            return {
                                maxDaysExceeded: {
                                    max: maxDays,
                                    actual: selectedDates.length
                                }
                            };
                        }

                        return null; // Valid
                    }
                ]
            ],
            quantity: this.quantityControl
        });

        this.quotationForm = this.fb.group({
            description: ['']
        });

        // Subscribe to form value changes for real-time updates
        this.bookingForm
            .get('selectedDates')
            ?.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(dates => {
                this.selectedDates = dates || [];
                this.calculatePrice();
                this.updateCalendarDays();
                // Notify parent component of selection changes
                this.selectedDatesChanged.emit(dates || []);
            });

        // Subscribe to quantity changes
        this.quantityControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(quantity => {
                this.calculatePrice();
                this.updateCalendarDays(); // Refresh calendar to show availability based on new quantity
            });
    }

    /**
     * Updates the quantity control when product changes
     */
    public updateQuantityControl() {
        if (this.quantityControl && this.product?.quantity) {
            const min = this.product.quantity.min || 1;
            const max = this.product.quantity.max || 99;
            const step = this.product.quantity.step || 1;

            // Update validators
            this.quantityControl.setValidators([
                Validators.min(min),
                Validators.max(max),
                Validators.required
            ]);

            // Set initial value if current value is out of bounds
            const currentValue = this.quantityControl.value;
            if (currentValue < min || currentValue > max) {
                this.quantityControl.setValue(min);
            }

            this.quantityControl.updateValueAndValidity();
        }
    }

    /**
     * Loads available dates from the API for the next 3 months
     *
     * This method:
     * 1. Constructs the API request with product identifier and date range
     * 2. Calls the backend availability checking service
     * 3. Processes the response to build availability data structures
     * 4. Generates calendar months for display
     * 5. Handles errors and loading states
     *
     * API Response Processing:
     * - Extracts available dates array
     * - Processes unavailable dates with reasons
     * - Calculates per-date quantity availability
     * - Updates calendar display data
     */
    public loadAvailableDates() {
        // Get product identifier - try multiple possible fields
        const productSlug =
            (this.product as any)?.seo?.slug ||
            (this.product as any)?.slug ||
            this.product?.sku ||
            String(this.product?.id);

        if (!productSlug) {
            this.toastr.error('Product identifier not found');
            return;
        }

        this.loading = true;

        // Load data for next 3 months starting from today
        const startDate = moment().format('YYYY-MM-DD');
        const endDate = moment()
            .add(this.monthsToGenerate, 'months')
            .format('YYYY-MM-DD');

        // Make API call to get availability data
        this.bookingService
            .getAvailableDates(
                productSlug,
                startDate,
                endDate,
                this.selectedQuantity
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: response => {
                    if (response.code === 200) {
                        this.availableDates = response.availableDates || [];
                        this.unavailableDates = response.unavailableDates || [];

                        // Process the response to build quantity tracking
                        this.processAvailabilityByDate(response);
                    } else {
                        this.toastr.error(this.translate.instant(response.msg));
                        this.resetAvailabilityData();
                    }
                    this.generateCalendarMonths();
                    this.debugAvailability();
                    this.loading = false;
                },
                error: error => {
                    this.toastr.error('Failed to load available dates');
                    this.resetAvailabilityData();
                    this.generateCalendarMonths();
                    this.loading = false;
                }
            });
    }

    /**
     * Processes the API response to build detailed availability information
     */
    private processAvailabilityByDate(response: any) {
        this.availableQuantityByDate = {};

        // If product has zero quantity, no dates should be available
        if (this.productQuantity <= 0) {
            this.availableDates = [];
            this.unavailableDates = response.unavailableDates || [];
            return;
        }

        // Create a set of all dates mentioned in the response
        const allDates = new Set([
            ...(response.availableDates || []),
            ...(response.unavailableDates || []).map(ud => ud.date)
        ]);

        // Initialize all dates with zero quantity first
        allDates.forEach(date => {
            this.availableQuantityByDate[date] = 0;
        });

        // Process available dates - set to full product quantity
        if (response.availableDates && Array.isArray(response.availableDates)) {
            response.availableDates.forEach(date => {
                // For fully available dates (no existing bookings), set to full product quantity
                // But only if product has quantity > 0
                if (this.productQuantity > 0) {
                    this.availableQuantityByDate[date] = this.productQuantity;
                }
            });
        }

        // Process unavailable dates to get exact remaining quantities
        if (
            response.unavailableDates &&
            Array.isArray(response.unavailableDates)
        ) {
            response.unavailableDates.forEach((unavailableDate: any) => {
                const date = unavailableDate.date;

                if (unavailableDate.reason === 'partially_booked') {
                    // Date has some items booked but still has remaining quantity
                    const remainingQuantity =
                        unavailableDate.remainingQuantity || 0;
                    // But cannot exceed product quantity
                    this.availableQuantityByDate[date] = Math.min(
                        remainingQuantity,
                        this.productQuantity
                    );
                } else if (unavailableDate.reason === 'fully_booked') {
                    // Date is fully booked - no quantity available
                    this.availableQuantityByDate[date] = 0;
                } else {
                    // For other reasons (advance booking, past date, etc.),
                    // these dates are blocked for business reasons but might have quantity
                    // Don't override the quantity - let it remain as set above
                }
            });
        }

        // Update the available dates array based on final quantities
        this.availableDates = [];
        Object.keys(this.availableQuantityByDate).forEach(date => {
            if (this.availableQuantityByDate[date] > 0) {
                this.availableDates.push(date);
            }
        });

        // Clean up - remove dates with zero or negative availability
        Object.keys(this.availableQuantityByDate).forEach(date => {
            if (this.availableQuantityByDate[date] <= 0) {
                delete this.availableQuantityByDate[date];
            }
        });
    }

    /**
     * Helper method to reset availability data structures
     * Used when API calls fail or return errors
     */
    private resetAvailabilityData() {
        this.availableDates = [];
        this.unavailableDates = [];
        this.availableQuantityByDate = {};
    }

    /**
     * Gets the available quantity for a specific date
     *
     * @param date - Date in YYYY-MM-DD format
     * @returns Number of items available for booking on that date
     */
    getAvailableQuantityForDate(date: string): number {
        return this.availableQuantityByDate[date] || 0;
    }

    /**
     * Generates calendar months for the next 3 months
     *
     * This method:
     * 1. Creates 3 calendar months starting from current month
     * 2. Each month includes all days with availability information
     * 3. Handles month boundaries and padding days
     * 4. Updates the calendar display data
     */
    public generateCalendarMonths() {
        this.calendarMonths = [];

        // Get the current date and ensure we start from current month
        const today = moment().locale('en');
        const currentMonth = today.clone().startOf('month');

        // Generate 3 months of calendar data starting from current month
        for (let i = 0; i < this.monthsToGenerate; i++) {
            const month = currentMonth.clone().add(i, 'month');
            this.calendarMonths.push(this.createCalendarMonth(month));
        }

        // Reset to first month (current month)
        this.currentMonthIndex = 0;
    }

    /**
     * Creates a single calendar month with all day information
     */
    private createCalendarMonth(month: moment.Moment): CalendarMonth {
        // Ensure we're working with the correct month and locale
        const targetMonth = month
            .clone()
            .locale('en')
            .startOf('month');

        const monthName = targetMonth.format('MMMM');
        const year = targetMonth.year();
        const monthIndex = targetMonth.month(); // 0-based (0 = January)
        const days: CalendarDay[] = [];

        // Get month information
        const firstDayOfMonth = targetMonth.clone().startOf('month');
        const daysInMonth = targetMonth.daysInMonth();
        const startDayOfWeek = firstDayOfMonth.day(); // 0 = Sunday, 6 = Saturday

        // Add padding days from previous month (for calendar grid alignment)
        if (startDayOfWeek > 0) {
            const prevMonth = targetMonth.clone().subtract(1, 'month');
            const daysInPrevMonth = prevMonth.daysInMonth();

            for (let i = startDayOfWeek - 1; i >= 0; i--) {
                const day = daysInPrevMonth - i;
                const date = prevMonth.clone().date(day);
                const dateStr = date.format('YYYY-MM-DD');

                days.push({
                    date: dateStr,
                    day: day,
                    isAvailable: false,
                    isSelected: false,
                    isUnavailable: true,
                    isToday: false,
                    isCurrentMonth: false
                });
            }
        }

        // Add current month days (the main content)
        for (let day = 1; day <= daysInMonth; day++) {
            const date = targetMonth.clone().date(day);
            const dateStr = date.format('YYYY-MM-DD');

            // Check if this is today (using English locale for consistency)
            const today = moment()
                .locale('en')
                .startOf('day');
            const isToday = date.isSame(today, 'day');

            // Apply business rules for availability
            if (this.productQuantity <= 0) {
                days.push({
                    date: dateStr,
                    day: day,
                    isAvailable: false,
                    isSelected: false,
                    isUnavailable: true,
                    isToday: isToday,
                    isCurrentMonth: true
                });
                continue;
            }

            // Check advance booking requirements
            const advanceBookingDays =
                this.product?.booking?.advanceBookingDays || 0;
            const minBookingDate = today
                .clone()
                .add(advanceBookingDays + 1, 'days');
            const isBeforeMinimumDate = date.isBefore(minBookingDate);

            // Check quantity availability
            const availableQuantity = this.getAvailableQuantityForDate(dateStr);
            const hasAvailableQuantity = availableQuantity > 0;
            const isSelected = this.selectedDates.includes(dateStr);

            // Check max days per user limit
            const isMaxDaysReached =
                this.maxDaysPerUser > 0 &&
                this.selectedDates.length >= this.maxDaysPerUser &&
                !isSelected;

            // Determine final availability status
            let isAvailable = false;
            let isUnavailable = false;

            if (isBeforeMinimumDate) {
                isUnavailable = true;
            } else if (isMaxDaysReached) {
                isUnavailable = true;
            } else if (hasAvailableQuantity) {
                isAvailable = true;
            } else {
                isUnavailable = true;
            }

            days.push({
                date: dateStr,
                day: day,
                isAvailable: isAvailable,
                isSelected: isSelected,
                isUnavailable: isUnavailable,
                isToday: isToday,
                isCurrentMonth: true
            });
        }

        // Add padding days from next month to complete the 6-week grid (42 days total)
        const totalDaysNeeded = 42;
        const remainingDays = totalDaysNeeded - days.length;

        if (remainingDays > 0) {
            const nextMonth = targetMonth.clone().add(1, 'month');

            for (let day = 1; day <= remainingDays; day++) {
                const date = nextMonth.clone().date(day);
                const dateStr = date.format('YYYY-MM-DD');

                days.push({
                    date: dateStr,
                    day: day,
                    isAvailable: false,
                    isSelected: false,
                    isUnavailable: true,
                    isToday: false,
                    isCurrentMonth: false
                });
            }
        }

        return {
            month: monthName,
            year: year,
            days: days,
            monthIndex: monthIndex
        };
    }

    /**
     * Updates calendar days after selection changes
     * Only updates the selection state without full regeneration
     */
    public updateCalendarDays() {
        // Instead of regenerating the entire calendar, just update the selection state
        this.calendarMonths.forEach(month => {
            month.days.forEach(day => {
                if (day.isCurrentMonth) {
                    day.isSelected = this.selectedDates.includes(day.date);
                }
            });
        });
    }

    // === CALENDAR NAVIGATION METHODS ===

    /**
     * Navigates to the previous month in the calendar
     * Only works if not already at the first month
     */
    goToPreviousMonth() {
        if (this.currentMonthIndex > 0) {
            this.currentMonthIndex--;
        }
    }

    /**
     * Navigates to the next month in the calendar
     * Only works if not already at the last month
     */
    goToNextMonth() {
        if (this.currentMonthIndex < this.calendarMonths.length - 1) {
            this.currentMonthIndex++;
        }
    }

    /**
     * Checks if user can navigate to previous month
     * @returns true if previous month navigation is available
     */
    canGoPrevious(): boolean {
        return this.currentMonthIndex > 0;
    }

    /**
     * Checks if user can navigate to next month
     * @returns true if next month navigation is available
     */
    canGoNext(): boolean {
        return this.currentMonthIndex < this.calendarMonths.length - 1;
    }

    /**
     * Gets the currently displayed month
     * @returns CalendarMonth object for the current month
     */
    getCurrentMonth(): CalendarMonth {
        return (
            this.calendarMonths[this.currentMonthIndex] ||
            this.calendarMonths[0]
        );
    }

    /**
     * Gets translated month and year for calendar display
     */
    getTranslatedMonthYear(): string {
        const currentMonth = this.getCurrentMonth();
        if (!currentMonth) return '';

        const currentLang = this.translate.currentLang || 'en';

        // Create a new moment instance with specific locale instead of setting global locale
        const date = moment()
            .month(currentMonth.monthIndex)
            .year(currentMonth.year)
            .locale(currentLang);

        return date.format('MMMM YYYY');
    }

    /**
     * Gets translated weekday headers
     */
    getTranslatedWeekDays(): string[] {
        const currentLang = this.translate.currentLang || 'en';

        if (currentLang === 'ar') {
            return [
                'الأحد',
                'الإثنين',
                'الثلاثاء',
                'الأربعاء',
                'الخميس',
                'الجمعة',
                'السبت'
            ];
        } else {
            return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        }
    }

    /**
     * Formats a date for display in selected dates list
     */
    getFormattedDate(dateString: string): string {
        const currentLang = this.translate.currentLang || 'en';

        // Create moment instance with specific locale instead of setting global locale
        const date = moment(dateString).locale(currentLang);

        if (currentLang === 'ar') {
            return date.format('dddd، D MMMM');
        } else {
            return date.format('ddd, MMM D');
        }
    }

    /**
     * Gets translated price label
     */
    getPriceLabel(): string {
        const dateCount = this.selectedDates.length;
        const quantity = this.selectedQuantity;
        const pricePerDay = this.product?.price || 0;

        if (dateCount === 1) {
            return this.translate.instant('per_day_price_with_qty', {
                dateCount,
                quantity,
                pricePerDay: this.formatCurrency(pricePerDay)
            });
        } else {
            return this.translate.instant('per_day_price_plural_with_qty', {
                dateCount,
                quantity,
                pricePerDay: this.formatCurrency(pricePerDay)
            });
        }
    }

    /**
     * Removes a specific date from the selection
     * Used by the selected dates list for individual date removal
     *
     * @param dateToRemove - Date string to remove from selection
     */
    removeDate(dateToRemove: string) {
        const currentDates = this.bookingForm.get('selectedDates')?.value || [];
        const updatedDates = currentDates.filter(d => d !== dateToRemove);
        this.bookingForm.get('selectedDates')?.setValue(updatedDates);
    }

    /**
     * Clears all selected dates
     */
    clearSelection() {
        // Clear selection data
        this.selectedDates = [];
        this.totalPrice = 0;

        // Update form
        this.bookingForm
            .get('selectedDates')
            ?.setValue([], { emitEvent: true });
    }

    /**
     * Formats currency for display
     */
    private formatCurrency(amount: number): string {
        if (!amount) return '';
        return amount.toFixed(3) + ' ' + 'BHD';
    }

    /**
     * Handles date selection/deselection in the calendar
     *
     * @param day - CalendarDay object representing the clicked date
     */
    onDateSelected(day: CalendarDay) {
        // Validate that the day can be interacted with
        if (!day.isCurrentMonth || day.isUnavailable) {
            return;
        }

        const currentDates = this.bookingForm.get('selectedDates')?.value || [];

        if (currentDates.includes(day.date)) {
            // Remove date from selection
            const updatedDates = currentDates.filter(d => d !== day.date);
            this.bookingForm.get('selectedDates')?.setValue(updatedDates);
        } else {
            // Check if date can accommodate the selected quantity
            if (!this.canDateAccommodateQuantity(day.date)) {
                this.toastr.warning(
                    this.translate.instant('insufficient_quantity_for_date', {
                        date: this.getFormattedDate(day.date),
                        available: this.availableQuantityByDate[day.date] || 0,
                        required: this.selectedQuantity
                    })
                );
                return;
            }

            // Check if adding this date would exceed the maximum allowed
            if (
                this.maxDaysPerUser > 0 &&
                currentDates.length >= this.maxDaysPerUser
            ) {
                this.toastr.warning(
                    this.translate.instant('max_days_exceeded', {
                        max: this.maxDaysPerUser
                    })
                );
                return;
            }

            // Add date and sort chronologically
            const updatedDates = [...currentDates, day.date].sort();
            this.bookingForm.get('selectedDates')?.setValue(updatedDates);
        }
    }

    /**
     * Calculates the total price based on selected dates
     *
     * Price Calculation Logic:
     * - Each selected date costs the product's taxed price (after discount)
     * - Formula: (taxedPrice || price) × number of selected dates × quantity
     * - Total = daily rate × selected dates × quantity per date
     *
     * Updates:
     * - Internal totalPrice property
     * - Emits priceCalculated event for parent component
     */
    public calculatePrice() {
        if (
            !this.selectedDates.length ||
            (!this.product?.price && !this.product?.taxedPrice)
        ) {
            this.totalPrice = 0;
            this.priceCalculated.emit(0);
            return;
        }

        // Calculate total price: (daily rate × number of selected dates) × quantity
        // Use taxedPrice (after discount) if available, otherwise use base price
        const dailyRate = this.product.taxedPrice || this.product.price || 0;
        this.totalPrice =
            dailyRate * this.selectedDates.length * this.selectedQuantity;

        // Emit the calculated price for parent component
        this.priceCalculated.emit(this.totalPrice);
    }

    /**
     * Creates a new booking or quotation request or adds to cart
     */
    createBooking() {
        // Check if at least one date is selected first
        if (!this.selectedDates.length) {
            this.toastr.error(this.translate.instant('please_select_date'));
            return;
        }

        // Check for max days exceeded
        if (
            this.maxDaysPerUser > 0 &&
            this.selectedDates.length > this.maxDaysPerUser
        ) {
            this.toastr.error(
                this.translate.instant('max_days_exceeded', {
                    max: this.maxDaysPerUser
                })
            );
            return;
        }

        // Check if product has available quantity
        if (this.productQuantity <= 0) {
            this.toastr.error(this.translate.instant('out_of_stock'));
            return;
        }

        // Validate that all selected dates still have availability
        const invalidDates = this.selectedDates.filter(date => {
            const availableQuantity = this.getAvailableQuantityForDate(date);
            return availableQuantity <= 0;
        });

        if (invalidDates.length > 0) {
            this.toastr.error(
                this.translate.instant('dates_no_longer_available', {
                    dates: invalidDates.join(', ')
                })
            );
            // Remove invalid dates from selection
            const validDates = this.selectedDates.filter(
                date => !invalidDates.includes(date)
            );
            this.bookingForm.get('selectedDates')?.setValue(validDates);
            return;
        }

        // Check if user is authenticated for quotation requests
        if (this.isQuotable && !this.authService.currentUserValue) {
            this.router.navigate(['/login'], {
                queryParams: { returnUrl: this.router.url }
            });
            return;
        }

        if (this.isQuotable) {
            this.showQuotationForm = true;
        } else {
            // For non-quotation products, add to cart instead of direct booking
            this.loading = true;
            this.addBookingToCart();
        }
    }

    /**
     * Adds the per-day booking item to cart
     */
    private addBookingToCart() {
        try {
            // Manual FormData creation to ensure proper structure
            const formData = new FormData();

            // Add base properties
            formData.append('productId', this.product._id);
            formData.append('amount', this.selectedQuantity.toString());

            // Add booking properties directly as individual fields
            formData.append('bookingType', 'perDay');
            formData.append('startDate', this.selectedDates[0]); // First selected date
            formData.append(
                'endDate',
                this.selectedDates[this.selectedDates.length - 1]
            ); // Last selected date
            formData.append('totalDays', this.selectedDates.length.toString());
            formData.append(
                'bookingQuantity',
                this.selectedQuantity.toString()
            );
            formData.append('totalPrice', this.totalPrice.toString());

            // Add booking dates as individual entries
            this.selectedDates.forEach((date, index) => {
                formData.append(`bookingDates[${index}]`, date);
            });

            // Also add as JSON string for backward compatibility
            const bookingData = {
                bookingType: 'perDay',
                selectedDates: this.selectedDates,
                totalDays: this.selectedDates.length,
                quantity: this.selectedQuantity,
                totalPrice: this.totalPrice,
                dates: this.selectedDates
            };
            formData.append('bookingData', JSON.stringify(bookingData));

            this.cartService.addItem(formData, this.product).subscribe({
                next: (response: any) => {
                    if (response.cart) {
                        // Log the specific item that was added
                        if (response.cart.items) {
                            const addedItem =
                                response.cart.items[
                                    response.cart.items.length - 1
                                ];
                        }
                    }

                    this.loading = false;

                    if (response.msg && response.code === 400) {
                        this.toastr.error(response.msg);
                        return;
                    }

                    // Handle cart warnings
                    if (response.cart?.warnings?.length > 0) {
                        response.cart.warnings.forEach(warning => {
                            this.toastr.warning(
                                warning.description[this.translate.currentLang],
                                warning.title[this.translate.currentLang]
                            );
                        });
                    }

                    // Handle cart errors
                    if (response.cart?.errors?.length > 0) {
                        response.cart.errors.forEach(error => {
                            this.toastr.error(
                                error.description[this.translate.currentLang],
                                error.title[this.translate.currentLang]
                            );
                        });
                        return;
                    }

                    // Success
                    this.toastr.success(
                        this.translate.instant(
                            'per_day_booking_added_to_cart_success',
                            {
                                productName:
                                    this.product.name[
                                        this.translate.currentLang
                                    ] || this.product.name,
                                totalDays: this.selectedDates.length,
                                quantity: this.selectedQuantity
                            }
                        )
                    );

                    this.addedToCart.emit({
                        cartItem: {
                            productId: this.product._id,
                            amount: this.selectedQuantity,
                            bookingType: 'perDay',
                            startDate: this.selectedDates[0],
                            endDate: this.selectedDates[
                                this.selectedDates.length - 1
                            ],
                            totalDays: this.selectedDates.length,
                            bookingQuantity: this.selectedQuantity,
                            totalPrice: this.totalPrice,
                            bookingDates: this.selectedDates,
                            bookingData: bookingData
                        },
                        response
                    });

                    this.resetForm();
                },
                error: (error: any) => {
                    this.loading = false;
                    console.error(
                        'Failed to add per-day booking to cart:',
                        error
                    );
                    this.toastr.error(
                        this.translate.instant('failed_to_add_booking_to_cart')
                    );
                }
            });
        } catch (error) {
            this.loading = false;
            console.error('Error preparing per-day booking for cart:', error);
            this.toastr.error(
                this.translate.instant('failed_to_prepare_booking')
            );
        }
    }

    /**
     * Buy Now for per-day bookings: add booking to cart and immediately checkout only that booking.
     * Creates a new single-checkout cart on the backend (via formData singleCheckout flag).
     */
    public buyNow() {
        if (this.loading) return;
        if (!this.selectedDates || this.selectedDates.length === 0) {
            this.toastr.error(this.translate.instant('please_select_date'));
            return;
        }
        if (this.isQuotable) {
            // Quotable flow handled elsewhere
            this.toastr.error(this.translate.instant('cannot_buy_quotation'));
            return;
        }

        this.loading = true;

        try {
            const formData = new FormData();
            formData.append('productId', String(this.product._id));
            formData.append('amount', String(this.selectedQuantity));
            formData.append('bookingType', 'perDay');
            formData.append('startDate', this.selectedDates[0]);
            formData.append(
                'endDate',
                this.selectedDates[this.selectedDates.length - 1]
            );
            formData.append('totalDays', String(this.selectedDates.length));
            formData.append('bookingQuantity', String(this.selectedQuantity));
            // use set to avoid duplicate array values
            formData.set('totalPrice', String(this.totalPrice));
            // add bookingDates
            this.selectedDates.forEach((date, index) =>
                formData.append(`bookingDates[${index}]`, date)
            );
            const bookingData = {
                bookingType: 'perDay',
                selectedDates: this.selectedDates,
                totalDays: this.selectedDates.length,
                quantity: this.selectedQuantity,
                totalPrice: this.totalPrice,
                dates: this.selectedDates
            };
            formData.set('bookingData', JSON.stringify(bookingData));

            // mark single-checkout so backend creates an isolated cart for immediate checkout
            formData.set('singleCheckout', 'true');

            this.cartService.addItem(formData, this.product).subscribe({
                next: (response: any) => {
                    this.loading = false;

                    if (response.msg && response.code === 400) {
                        this.toastr.error(response.msg);
                        return;
                    }

                    if (response.cart?.warnings?.length > 0) {
                        response.cart.warnings.forEach(w =>
                            this.toastr.warning(
                                w.description[this.translate.currentLang],
                                w.title[this.translate.currentLang]
                            )
                        );
                    }

                    if (response.cart?.errors?.length > 0) {
                        response.cart.errors.forEach(e =>
                            this.toastr.error(
                                e.description[this.translate.currentLang],
                                e.title[this.translate.currentLang]
                            )
                        );
                        return;
                    }

                    const returnedCart = response.cart;
                    if (!returnedCart || !returnedCart._id) {
                        this.toastr.error(
                            this.translate.instant('failed_to_add_to_cart')
                        );
                        return;
                    }

                    // Robust matching: attempt exact match by productId + bookingType + bookingDates + bookingQuantity + totalPrice
                    const normalizedSelectedDates = (
                        this.selectedDates || []
                    ).map(String);
                    const addedItem = _.find(
                        returnedCart.items || [],
                        (it: any) => {
                            try {
                                if (
                                    String(it.productId) !==
                                    String(this.product._id)
                                )
                                    return false;
                                if (it.bookingType !== 'perDay') return false;
                                const itemDates =
                                    (it.bookingDates &&
                                        it.bookingDates.map(String)) ||
                                    (it.bookingData &&
                                        it.bookingData.dates &&
                                        it.bookingData.dates.map(String)) ||
                                    [];
                                // compare dates (order should match); fallback to set-equality if needed
                                const datesEqual =
                                    _.isEqual(
                                        itemDates,
                                        normalizedSelectedDates
                                    ) ||
                                    _.isEqual(
                                        _.sortBy(itemDates),
                                        _.sortBy(normalizedSelectedDates)
                                    );
                                if (!datesEqual) return false;
                                if (
                                    (it.bookingQuantity || it.amount || 0) !==
                                    (this.selectedQuantity || 0)
                                )
                                    return false;
                                // compare numeric totals with tolerance
                                const itemTotalPrice = Number(
                                    it.totalPrice ??
                                        it.total ??
                                        it.unitTotal ??
                                        0
                                );
                                if (
                                    Math.abs(
                                        itemTotalPrice -
                                            Number(this.totalPrice || 0)
                                    ) > 0.0001
                                )
                                    return false;
                                return true;
                            } catch (e) {
                                return false;
                            }
                        }
                    );

                    // fallback: pick last cart item with same productId and bookingType if exact match not found
                    const fallback = _.findLast(
                        returnedCart.items || [],
                        (it: any) =>
                            String(it.productId) === String(this.product._id) &&
                            it.bookingType === 'perDay'
                    );

                    const targetItem = addedItem || fallback;
                    if (!targetItem) {
                        // As fallback navigate checkout for cart (no selected items)
                        this.toastr.success(
                            this.translate.instant('added_to_cart')
                        );
                        const params = {
                            cartId: returnedCart._id,
                            itemIds: []
                        };
                        this.cartService.userCart = {
                            cartId: returnedCart._id,
                            items: []
                        };
                        this.cartService.saveCartId(params).subscribe(() => {
                            this.router.navigate(['/checkout'], {
                                state: { data: params }
                            });
                        });
                        return;
                    }

                    // Checkout only the found item
                    this.checkoutSingleItem(
                        returnedCart._id,
                        targetItem.itemId
                    );
                },
                error: (err: any) => {
                    this.loading = false;
                    console.error('BuyNow addBookingToCart error', err);
                    this.toastr.error(
                        this.translate.instant('failed_to_add_booking_to_cart')
                    );
                }
            });
        } catch (err) {
            this.loading = false;
            console.error('BuyNow prepare error', err);
            this.toastr.error(
                this.translate.instant('failed_to_prepare_booking')
            );
        }
    }

    /**
     * Checkout a single item from a cart (sets userCart and navigates to checkout)
     */
    private checkoutSingleItem(cartId: string, itemId: string) {
        const items = [itemId];
        const params = { cartId, itemIds: items };
        this.cartService.userCart = { cartId, items };
        this.cartService.saveCartId(params).subscribe({
            next: () => {
                this.router.navigate(['/checkout'], {
                    state: { data: params }
                });
            },
            error: () => {
                this.router.navigate(['/checkout'], {
                    state: { data: params }
                });
            }
        });
    }

    /**
     * Closes the quotation form modal
     */
    closeQuotationForm(): void {
        this.showQuotationForm = false;
        this.quotationForm.reset();
    }

    /**
     * Submits the quotation request with user inputs
     */
    submitQuotationRequest(): void {
        this.loading = true;
        this.createQuotationRequest();
    }

    /**
     * Creates an actual booking with quantity
     */
    private createActualBooking() {
        // Get company name first, then create booking
        this.companiesService.getCompanyName(this.product.companyId).subscribe({
            next: companyName => {
                console.log('Company Name:', companyName);

                // Now create booking with the resolved company name and quantity
                const bookingData = {
                    productId: String(
                        (this.product as any)._id || this.product?.id
                    ),
                    companyId: this.product.companyId,
                    bookingType: 'perDay' as const,
                    bookingDates: this.selectedDates,
                    startDate: this.selectedDates[0],
                    quantity: this.selectedQuantity, // Include quantity
                    specialRequests: '',
                    customerName: '',
                    customerEmail: '',
                    totalPrice: this.totalPrice,
                    productName:
                        typeof this.product.name === 'object' &&
                        this.product.name !== null
                            ? {
                                  en:
                                      (this.product.name as {
                                          en?: string;
                                          ar?: string;
                                      }).en || '',
                                  ar:
                                      (this.product.name as {
                                          en?: string;
                                          ar?: string;
                                      }).ar || ''
                              }
                            : {
                                  en: String(this.product.name || ''),
                                  ar: String(this.product.name || '')
                              },
                    companyName: {
                        en: companyName.en || '',
                        ar: companyName.ar || ''
                    }
                };

                console.log('Booking data with quantity:', bookingData);

                // Send booking request to backend
                this.bookingService
                    .createBooking(bookingData)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: response => {
                            this.loading = false;
                            if (response.code === 200) {
                                this.toastr.success(
                                    this.translate.instant(
                                        'booking_request_created'
                                    )
                                );
                                this.bookingCreated.emit(response.booking);
                                this.resetForm();
                                this.loadAvailableDates();
                            } else {
                                this.toastr.error(
                                    response.msg ||
                                        this.translate.instant('No good')
                                );
                            }
                        },
                        error: error => {
                            this.loading = false;
                            this.toastr.error(
                                this.translate.instant('booking_network_error')
                            );
                        }
                    });
            },
            error: error => {
                console.error('Failed to get company name:', error);
                this.loading = false;
                this.toastr.error(
                    this.translate.instant('failed_to_get_company_info')
                );
            }
        });
    }

    /**
     * Creates a quotation request with quantity information
     */
    private createQuotationRequest() {
        const description = this.quotationForm.get('description')?.value || '';

        const quotationData = {
            product: {
                _id: (this.product as any)._id || this.product?.id,
                name:
                    typeof this.product.name === 'object' &&
                    this.product.name !== null
                        ? {
                              en:
                                  (this.product.name as {
                                      en?: string;
                                      ar?: string;
                                  }).en || '',
                              ar:
                                  (this.product.name as {
                                      en?: string;
                                      ar?: string;
                                  }).ar || ''
                          }
                        : {
                              en: String(this.product.name || ''),
                              ar: String(this.product.name || '')
                          },
                qty: this.selectedQuantity, // Include quantity in quotation
                bookingDetails: {
                    type: 'perDay',
                    selectedDates: this.selectedDates,
                    totalDays: this.selectedDates.length,
                    quantity: this.selectedQuantity // Include quantity in booking details
                }
            },
            companyId:
                this.product?.companyId ||
                (this.product as any)?.companyData?._id,
            description: description
        };

        this.quotationRequestService.singleRequest(quotationData).subscribe({
            next: (response: any) => {
                this.loading = false;
                this.showQuotationForm = false;
                if (response && response.code !== 400) {
                    if (response.addedNotification) {
                        this.authService.updateNotifications({
                            added: response.addedNotification
                        });
                    }
                    this.toastr.success(
                        this.translate.instant(
                            'quotation_request_sent_successfully'
                        )
                    );
                    this.quotationRequested.emit(response);
                    this.resetForm();
                    this.loadAvailableDates();
                } else {
                    this.toastr.error(
                        response?.msg ||
                            this.translate.instant(
                                'failed_to_send_quotation_request'
                            )
                    );
                }
            },
            error: (error: any) => {
                this.loading = false;
                this.showQuotationForm = false;
                this.toastr.error(
                    this.translate.instant('quotation_request_network_error')
                );
            }
        });
    }

    /**
     * Marks all form controls as touched to trigger validation display
     * Used when form is invalid and user tries to submit
     */
    private markFormGroupTouched() {
        Object.keys(this.bookingForm.controls).forEach(key => {
            this.bookingForm.get(key)?.markAsTouched();
        });
    }

    /**
     * Resets the form to its initial state after successful booking
     */
    public resetForm() {
        this.selectedDates = [];
        this.totalPrice = 0;

        // Reset form without triggering events
        this.bookingForm
            .get('selectedDates')
            ?.setValue([], { emitEvent: false });
        this.bookingForm.get('selectedDates')?.markAsUntouched();
        this.bookingForm.get('selectedDates')?.markAsPristine();

        // Force calendar regeneration to ensure clean state
        setTimeout(() => {
            this.generateCalendarMonths();
        }, 100);
    }

    // === UI HELPER METHODS ===

    /**
     * Generates constraint text for display to users
     *
     * Shows:
     * - Advance booking requirements
     * - Maximum days per user limits
     * - Approval requirements
     * - Dynamic remaining days counter
     *
     * @returns Formatted constraint text for UI display
     */
    getConstraintsText(): string {
        if (!this.product?.booking) return '';

        const constraints = [];

        // Advance booking requirement
        if (this.product.booking.advanceBookingDays > 0) {
            constraints.push(
                `${
                    this.product.booking.advanceBookingDays
                } ${this.translate.instant('advance_booking_required')}`
            );
        }

        // Maximum days per user limit
        if (this.product.booking.maxBookingsPerUser > 0) {
            const remaining =
                this.product.booking.maxBookingsPerUser -
                this.selectedDates.length;
            if (this.selectedDates.length > 0) {
                const maxText = this.translate.instant(
                    'maximum_days_per_booking',
                    {
                        max: this.product.booking.maxBookingsPerUser
                    }
                );
                const remainingText = this.translate.instant(
                    'maximum_days_remaining',
                    {
                        remaining: remaining
                    }
                );
                constraints.push(maxText + ' ' + remainingText);
            } else {
                constraints.push(
                    this.translate.instant('maximum_days_per_booking', {
                        max: this.product.booking.maxBookingsPerUser
                    })
                );
            }
        }

        // Approval requirement
        if (this.product.booking.requireApproval) {
            constraints.push(this.translate.instant('requires_approval'));
        }

        return constraints.join(' • ');
    }

    /**
     * Generates tooltip text for calendar days
     */
    getDateTitle(day: CalendarDay): string {
        if (!day.isCurrentMonth) {
            return this.translate.instant('previous_next_month');
        }

        if (day.isSelected) {
            return this.translate.instant('selected_date_remove');
        }

        if (day.isAvailable) {
            const availableQuantity = this.getAvailableQuantityForDate(
                day.date
            );
            if (availableQuantity === this.productQuantity) {
                return this.translate.instant('available_items_click', {
                    quantity: availableQuantity
                });
            } else {
                return this.translate.instant('available_items_remaining', {
                    quantity: availableQuantity
                });
            }
        }

        if (day.isUnavailable) {
            // Use English locale for date calculations
            const date = moment(day.date).locale('en');
            const advanceBookingDays =
                this.product?.booking?.advanceBookingDays || 0;
            const today = moment()
                .locale('en')
                .startOf('day');
            const minBookingDate = today
                .clone()
                .add(advanceBookingDays + 1, 'days');

            if (date.isSame(today)) {
                return this.translate.instant('today_not_available');
            }

            if (date.isBefore(today)) {
                return this.translate.instant('past_date');
            }

            if (date.isBefore(minBookingDate)) {
                return this.translate.instant('advance_booking_note', {
                    days: advanceBookingDays,
                    date: minBookingDate.format('MMM DD')
                });
            }

            if (
                this.maxDaysPerUser > 0 &&
                this.selectedDates.length >= this.maxDaysPerUser
            ) {
                return this.translate.instant('max_days_selected', {
                    max: this.maxDaysPerUser
                });
            }

            return this.translate.instant('unavailable_fully_booked');
        }

        return '';
    }

    ngOnInit() {
        this.monthsToGenerate = this.product?.booking?.monthsToGenerate || 3;
        this.loadAvailableDates();

        // Subscribe to language changes
        this.translate.onLangChange
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => {
                // Preserve current state
                const currentSelection = [...this.selectedDates];
                const currentAvailability = { ...this.availableQuantityByDate };
                const currentAvailableDates = [...this.availableDates];

                // Regenerate calendar with new language but preserve data
                this.generateCalendarMonths();

                // Restore state
                this.selectedDates = currentSelection;
                this.availableQuantityByDate = currentAvailability;
                this.availableDates = currentAvailableDates;

                // Update calendar display with preserved state
                this.updateCalendarDays();
            });
    }

    /**
     * Component destruction
     * Cleans up subscriptions to prevent memory leaks
     */
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // === DEBUG METHODS ===

    /**
     * Debug method for troubleshooting availability issues
     * Logs detailed information about availability state
     *
     * Logs:
     * - Available dates array
     * - Availability by date mapping
     * - Selected dates
     * - Product configuration
     * - Calendar state
     * - Specific date checks
     */
    public debugAvailability() {
        // console.log('=== DEBUG AVAILABILITY ===');
        // console.log('Available dates array:', this.availableDates);
        // console.log('Availability by date:', this.availableQuantityByDate);
        // console.log('Selected dates:', this.selectedDates);
        // console.log('Product quantity:', this.productQuantity);
        // console.log('Calendar months:', this.calendarMonths.length);
        // // Check current month days
        // if (this.calendarMonths.length > 0) {
        //     const currentMonth = this.getCurrentMonth();
        //     console.log('Current month days sample:');
        //     // Look for specific dates for debugging
        //     currentMonth.days.forEach(d => {
        //         if (d.isCurrentMonth && d.date.includes('2025-07-25')) {
        //             console.log(`Date ${d.date}:`, {
        //                 isAvailable: d.isAvailable,
        //                 isUnavailable: d.isUnavailable,
        //                 isCurrentMonth: d.isCurrentMonth,
        //                 availableQuantity: this.getAvailableQuantityForDate(
        //                     d.date
        //                 ),
        //                 isSelected: d.isSelected
        //             });
        //         }
        //     });
        // }
        // // Check specific date (July 25th example)
        // const july25 = '2025-07-25';
        // console.log(`July 25th specific check:`);
        // console.log(
        //     `- Available quantity: ${this.getAvailableQuantityForDate(july25)}`
        // );
        // console.log(
        //     `- In available dates: ${this.availableDates.includes(july25)}`
        // );
        // console.log(
        //     `- In quantity map: ${july25 in this.availableQuantityByDate}`
        // );
    }

    /**
     * Gets the list of month names being displayed for debugging
     */
    getDisplayedMonths(): string[] {
        return this.calendarMonths.map(month => `${month.month} ${month.year}`);
    }

    /**
     * Checks if a specific date can accommodate the selected quantity
     * @param date - Date string (YYYY-MM-DD)
     * @returns true if the available quantity for the date is >= selected quantity
     */
    canDateAccommodateQuantity(date: string): boolean {
        const availableQuantity = this.getAvailableQuantityForDate(date);
        return availableQuantity >= this.selectedQuantity;
    }
}
